import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);
import { Directive, Input, Host, Self, Optional } from "@angular/core";
import { NativeDesktopSupport } from "./native-desktop-support";
import { DomHandlerMode, DomHandlerService, DOM_HANDLER_MODE_TOKEN } from "@bfx/primeng/dom";
import { OverlayPanel } from "@bfx/primeng/overlaypanel";
import { ResizeObserverScheduler } from "../../shared/helper-services/resize-observer-scheduler";
import { MonitorInfo, Rect } from "openfin/_v2/api/system/monitor";
import { NativeDesktopWindow } from "../../shared/native-desktop/NativeDesktopWindow";
import { WindowType } from "shared/native-desktop/NativeDesktopWindowOptions";

/**
 * @deprecated please don't use this.
 * it only partially functions for order details contact info,
 * which is practically a dialog.
 */
@Directive({
  selector: "[nativeDesktopTooltip]",
  providers: [
    {
      provide: DOM_HANDLER_MODE_TOKEN,
      useFactory: () => {
        if (NativeDesktopSupport.isNativeDesktopContainer())
          return new DomHandlerService(DomHandlerMode.NativeContainer);
        return new DomHandlerService(DomHandlerMode.Browser);
      },
    },
  ],
  standalone: false,
})
export class NativeDesktopTooltip {
  private static tooltipWindow: NativeDesktopWindow = null;

  private static resizeObserver: ResizeObserverScheduler;

  private _parentElement: Element = null;
  private _screenBounds: Rect;

  constructor(@Host() @Self() @Optional() component: OverlayPanel) {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      this.initialize(component);
    }
  }

  private initialize(component: OverlayPanel): void {
    // FIXME: NG8
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      NativeDesktopTooltip.initWindow();
      if (component instanceof OverlayPanel) {
        let overlay: OverlayPanel = component as OverlayPanel;
        overlay.el.nativeElement.style.opacity = 0;
        overlay.showTransitionOptions = overlay.hideTransitionOptions = "0ms";
        overlay.onWindowResize = () => {
          /* NOOP */
        };

        let parentWindow = NativeDesktopSupport.getWindow(overlay.el.nativeElement);
        parentWindow.addEventListener("blurred", () => {
          this.onHideOverlay.call(this, overlay);
        });
        parentWindow.addEventListener("closed", this.onHideOverlay.bind(this, overlay));

        overlay.onShow.subscribe(
          function () {
            requestAnimationFrame(
              function () {
                this.onShowOverlay(overlay);
              }.bind(this),
            );
          }.bind(this),
        );

        overlay.onHide.subscribe(
          function () {
            requestAnimationFrame(
              function () {
                this.onHideOverlay(overlay);
              }.bind(this),
            );
          }.bind(this),
        );

        NativeDesktopSupport.desktop.api.getMonitorInfo().then((monitorInfo: MonitorInfo) => {
          this._screenBounds = monitorInfo.primaryMonitor.monitorRect;
        });
      }
    }
  }

  public onShowOverlay(overlayComponent: OverlayPanel): void {
    overlayComponent.appendTo = null;

    let element: Element = overlayComponent.el.nativeElement;
    element.removeAttribute("appendTo");
    element["style"].left = element["style"].top = "0px";
    element["style"].opacity = 1;

    let container: any = overlayComponent.container;
    if (container != null) {
      container.style.visibility = "hidden";
    }

    let window: NativeDesktopWindow = NativeDesktopTooltip.tooltipWindow;

    if (window == null) {
      logger.out.warn(
        `${this.constructor.name}.onShowOverlay() window is null. skipping adjustWindow`,
      );
      return;
    }

    let parent = NativeDesktopSupport.getWindow(overlayComponent.container);
    if (parent != null && parent != window) {
      window.setParentWindow(parent);
    }

    window.getNativeWindow().document.body.innerHTML = "";
    window.getNativeWindow().document.body.appendChild(element);

    requestAnimationFrame(() => {
      if (container != null) {
        element.appendChild(container);
        container.style.boxShadow = "none";
        this.adjustWindow(window, container, element, overlayComponent);
      }
    });
  }

  private adjustWindow(
    window: NativeDesktopWindow,
    container: HTMLDivElement,
    element: Element,
    overlayComponent: OverlayPanel,
  ): void {
    let width: number = container["offsetWidth"] * NativeDesktopSupport.zoomLevel;
    let height: number = container["offsetHeight"] * NativeDesktopSupport.zoomLevel;

    let parentWindow = NativeDesktopSupport.getWindow(this.parentElement);
    let bounds = container.getBoundingClientRect();
    window.getNativeWindow().document.body.appendChild(container);

    let x: number =
      parentWindow.getNativeWindow().screenX +
      (this.parentElement["offsetLeft"] + 16) * NativeDesktopSupport.zoomLevel;
    let y: number =
      parentWindow.getNativeWindow().screenY +
      (this.parentElement["offsetTop"] + 16) * NativeDesktopSupport.zoomLevel;

    let monitorInfo = NativeDesktopSupport.getMonitorInfo();

    let area = monitorInfo.virtualScreen;

    x = Math.max(x, area.left);
    y = Math.max(y, area.top);

    if (x + bounds.width * NativeDesktopSupport.zoomLevel > area.right) {
      x = area.right - bounds.width * NativeDesktopSupport.zoomLevel;
    }

    if (y + bounds.height * NativeDesktopSupport.zoomLevel > area.bottom) {
      y = area.bottom - bounds.height * NativeDesktopSupport.zoomLevel;
    }

    logger.out.info(
      `DESKTOP: NativeDesktopTooltip.adjustWindow(): ADJUST => calendarWindow '${window.getId()}' | parentWindow: '${parentWindow.getId()}' | bounds = [x: ${x}, y: ${y}, width: ${width}, height ${height}]`,
    );

    window.getNativeWindow().document.body.style.overflow = "hidden";
    window.setBounds(x, y, width, height).then(() => {
      window.getNativeWindow().document.body.appendChild(container);
      container["style"].visibility = "visible";
      container["style"].opacity = "1";
      window.show().then(() => {
        let parentWindow = NativeDesktopSupport.getWindow(this.parentElement);

        if (parentWindow == null) {
          logger.out.warn(`${this.constructor.name}.adjustWindow() failed to find parent window`);
          return;
        }
        requestAnimationFrame(() => {
          // TODO ELECTRON
          // let parentWindowRef = fin.Window.wrapSync({
          //   uuid: parentWindow.uuid,
          //   name: parentWindow.name,
          // });
          // if (parentWindowRef == null) {
          //   logger.out.warn(
          //     `${this.constructor.name}.adjustWindow() failed to find parent window ref`,
          //   );
          //   return;
          // }
          // parentWindowRef["prependOnceListener"](
          //   "bounds-changed",
          //   this.onHideOverlay.bind(this, overlayComponent),
          // );
        });
      });
      NativeDesktopTooltip.resizeObserver.observe(element);
    });
  }

  public onHideOverlay(overlayComponent: OverlayPanel): void {
    if (NativeDesktopTooltip.tooltipWindow) {
      let element: Element = overlayComponent.el.nativeElement;

      requestAnimationFrame(
        function () {
          if (element != null && element instanceof Element) {
            NativeDesktopTooltip.resizeObserver.unobserve(element);
          }

          let tooltipWindow: Window = element.ownerDocument.defaultView;
          if (tooltipWindow == NativeDesktopTooltip.tooltipWindow.getNativeWindow()) {
            NativeDesktopTooltip.tooltipWindow.hide();
          }
        }.bind(this),
      );
    }
  }

  public get parentElement(): Element {
    return this._parentElement;
  }

  @Input("nativeDesktopTooltip")
  public set parentElement(parentElement: Element) {
    this._parentElement = parentElement;
  }

  public static initWindow(): Promise<void> {
    if (
      NativeDesktopSupport.isNativeDesktopContainer() &&
      NativeDesktopTooltip.tooltipWindow == null
    ) {
      let title: string = "tooltip";

      NativeDesktopTooltip.resizeObserver = new ResizeObserverScheduler(
        (entries) => NativeDesktopTooltip.onResize(entries),
        NativeDesktopTooltip.name,
      );

      return new Promise<void>((res) =>
        NativeDesktopSupport.openWindow(
          title,
          WindowType.Utility,
          true,
          null,
          (window: NativeDesktopWindow) => {
            NativeDesktopTooltip.tooltipWindow = window;
            res();
          },
        ),
      );
    }
    return Promise.resolve();
  }

  public static onResize(entries: ResizeObserverEntry[]) {
    const resizePromises = [];

    for (const entry of entries) {
      let target: Element = entry.target;
      const { width, height } = entry.contentRect;
      const offsetHeight = target["offsetHeight"];
      const offsetWidth = target["offsetWidth"];
      if (NativeDesktopTooltip.tooltipWindow != null && width > 0 && height > 0) {
        let w: number = offsetWidth
          ? offsetWidth * NativeDesktopSupport.zoomLevel
          : (width + 13) * NativeDesktopSupport.zoomLevel;
        let h: number = offsetHeight
          ? offsetHeight * NativeDesktopSupport.zoomLevel
          : (height + 5) * NativeDesktopSupport.zoomLevel;

        resizePromises.push(
          new Promise<void>((res) => {
            NativeDesktopTooltip.tooltipWindow.setSize(w, h).then(
              () => {
                res();
              },
              (err) => {
                logger.out.error(`ResizeObserver: failed resizeTo() due to ${err}`);
                res();
              },
            );
          }),
        );
      }
    }
    return new Promise<void>((all) => {
      Promise.all(resizePromises).then(() => all);
    });
  }
}
